/**
 * PurchasesPage - User's order history and downloads
 */

import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Link, Navigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  Package,
  Download,
  Clock,
  ShoppingBag,
  Music,
  FileDown,
  CheckCircle,
  DollarSign,
  Loader2,
  ChevronRight,
  RefreshCw,
  ArrowLeft,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Stats card component
const StatCard = ({ icon: Icon, label, value, color }) => (
  <div className="p-4 bg-zinc-800/50 rounded-xl border border-zinc-800">
    <div className="flex items-center gap-3">
      <div className={`p-2 rounded-lg ${color}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <p className="text-xs text-zinc-500">{label}</p>
        <p className="text-xl font-bold text-white">{value}</p>
      </div>
    </div>
  </div>
);

// Order card component
const OrderCard = ({ order, onDownload }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-zinc-800/50 rounded-xl border border-zinc-800 overflow-hidden"
    >
      {/* Order header */}
      <div
        className="p-4 cursor-pointer hover:bg-zinc-800/80 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-2 bg-emerald-500/20 rounded-lg">
              <CheckCircle className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <p className="font-medium text-white">
                Order #{order.id?.slice(-8)?.toUpperCase()}
              </p>
              <p className="text-xs text-zinc-500">
                {new Date(order.completed_at).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-lg font-bold text-white">${order.total?.toFixed(2)}</p>
              <p className="text-xs text-zinc-500">{order.items?.length} item(s)</p>
            </div>
            <ChevronRight
              className={`w-5 h-5 text-zinc-500 transition-transform ${
                expanded ? 'rotate-90' : ''
              }`}
            />
          </div>
        </div>
      </div>

      {/* Order details (expandable) */}
      {expanded && (
        <div className="border-t border-zinc-800 p-4 space-y-3">
          {order.items?.map((item, index) => {
            const download = order.downloads?.find(
              (d) => d.product_id === item.product_id
            );

            return (
              <div
                key={index}
                className="flex items-center justify-between p-3 bg-zinc-900/50 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-violet-500/20 rounded-lg flex items-center justify-center">
                    <Music className="w-5 h-5 text-violet-400" />
                  </div>
                  <div>
                    <p className="font-medium text-white">{item.product_name}</p>
                    <p className="text-xs text-zinc-500">
                      Qty: {item.quantity} × ${item.price?.toFixed(2)}
                    </p>
                  </div>
                </div>
                
                {download ? (
                  <Button
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDownload(download.download_url, item.product_name);
                    }}
                    className="bg-emerald-500 hover:bg-emerald-600"
                    disabled={download.remaining_downloads <= 0}
                  >
                    <Download className="w-4 h-4 mr-1" />
                    {download.remaining_downloads > 0
                      ? `Download (${download.remaining_downloads} left)`
                      : 'No downloads left'}
                  </Button>
                ) : (
                  <span className="text-xs text-zinc-500">Processing...</span>
                )}
              </div>
            );
          })}
          
          <div className="flex items-center justify-between pt-3 border-t border-zinc-800 text-sm">
            <span className="text-zinc-500">Payment Method</span>
            <span className="text-white capitalize">
              {order.payment_method?.replace('_', ' ')}
            </span>
          </div>
        </div>
      )}
    </motion.div>
  );
};

// Main PurchasesPage component
const PurchasesPage = () => {
  const { user, loading: authLoading } = useAuth();
  const [orders, setOrders] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  const fetchData = useCallback(async () => {
    if (!user) return;

    setLoading(true);
    try {
      // Fetch orders and summary in parallel
      const [ordersRes, summaryRes] = await Promise.all([
        fetch(`${API_URL}/api/store/orders/${user.id}?page=${page}&limit=10`),
        fetch(`${API_URL}/api/store/orders/${user.id}/summary`),
      ]);

      if (ordersRes.ok) {
        const ordersData = await ordersRes.json();
        if (page === 1) {
          setOrders(ordersData.orders);
        } else {
          setOrders((prev) => [...prev, ...ordersData.orders]);
        }
        setHasMore(ordersData.orders.length === 10);
      }

      if (summaryRes.ok) {
        const summaryData = await summaryRes.json();
        setSummary(summaryData);
      }
    } catch (error) {
      console.error('Error fetching purchases:', error);
      toast.error('Failed to load purchases');
    } finally {
      setLoading(false);
    }
  }, [user, page]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDownload = async (downloadUrl, productName) => {
    try {
      window.open(`${API_URL}${downloadUrl}`, '_blank');
      toast.success(`Downloading ${productName}...`);
      // Refresh to update download counts
      setTimeout(fetchData, 1000);
    } catch (error) {
      toast.error('Download failed');
    }
  };

  // Redirect if not logged in
  if (!authLoading && !user) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      {/* Header */}
      <header className="border-b border-zinc-800 bg-zinc-900/50 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/store" className="p-2 hover:bg-zinc-800 rounded-lg transition-colors">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div>
                <h1 className="text-xl font-bold">My Purchases</h1>
                <p className="text-sm text-zinc-500">Order history and downloads</p>
              </div>
            </div>
            <Link to="/store">
              <Button className="bg-violet-500 hover:bg-violet-600">
                <ShoppingBag className="w-4 h-4 mr-2" />
                Continue Shopping
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {loading && page === 1 ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-10 h-10 text-violet-400 animate-spin" />
          </div>
        ) : (
          <>
            {/* Summary stats */}
            {summary && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                <StatCard
                  icon={Package}
                  label="Total Orders"
                  value={summary.total_orders}
                  color="bg-violet-500/20 text-violet-400"
                />
                <StatCard
                  icon={DollarSign}
                  label="Total Spent"
                  value={`$${summary.total_spent.toFixed(2)}`}
                  color="bg-emerald-500/20 text-emerald-400"
                />
                <StatCard
                  icon={Music}
                  label="Items Purchased"
                  value={summary.total_items_purchased}
                  color="bg-amber-500/20 text-amber-400"
                />
                <StatCard
                  icon={FileDown}
                  label="Available Downloads"
                  value={summary.available_downloads}
                  color="bg-blue-500/20 text-blue-400"
                />
              </div>
            )}

            {/* Orders list */}
            {orders.length === 0 ? (
              <div className="text-center py-16">
                <Package className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
                <h2 className="text-xl font-semibold mb-2">No purchases yet</h2>
                <p className="text-zinc-500 mb-6">
                  Browse our store to find amazing sample packs and plugins
                </p>
                <Link to="/store">
                  <Button className="bg-violet-500 hover:bg-violet-600">
                    <ShoppingBag className="w-4 h-4 mr-2" />
                    Browse Store
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold flex items-center gap-2">
                    <Clock className="w-5 h-5 text-violet-400" />
                    Order History
                  </h2>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setPage(1);
                      fetchData();
                    }}
                    className="border-zinc-700"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Refresh
                  </Button>
                </div>

                {orders.map((order) => (
                  <OrderCard
                    key={order.id}
                    order={order}
                    onDownload={handleDownload}
                  />
                ))}

                {hasMore && (
                  <Button
                    variant="outline"
                    onClick={() => setPage((p) => p + 1)}
                    className="w-full border-zinc-700"
                    disabled={loading}
                  >
                    {loading ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <ChevronRight className="w-4 h-4 mr-2" />
                    )}
                    Load More Orders
                  </Button>
                )}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default PurchasesPage;
